import React from 'react';
import { useTranslation } from 'react-i18next';
import { Notice, Button, Dropdown, Chip, IconButton, SkeletonCard, SkeletonLine } from '@origo/origo-ui';
import { FunnelIcon, ArrowsDownUpIcon, XIcon } from '@phosphor-icons/react';
import PlanSearchBar, { type PlanSearchBarProps } from './components/PlanSearchBar';
import PlanFilterPanel, { type PlanFilterPanelProps } from '../../filter/PlanFilterPanel';
import PlanResultCard from './components/PlanResultCard';
import LoadMoreTrigger from './components/LoadMoreTrigger';
import { sortOptions, type SortOption } from '../../filter/types';
import type { PlanSummary } from '../../types';
import styles from './PlanLandingPage.module.css';

export interface ActiveFilterChip {
    id: string;
    label: string;
    onRemove: () => void;
}

interface PlanLandingPageProps {
    onCreatePlan: () => void;
    search: PlanSearchBarProps;
    /** Props til filterpanelet i sidepanelet (showSortControl=false). */
    filter: PlanFilterPanelProps;
    filterOpen: boolean;
    onToggleFilter: () => void;
    activeFilterCount: number;
    activeFilterChips: ActiveFilterChip[];
    onClearAllFilters: () => void;
    sortOption: SortOption;
    onSortChange: (o: SortOption) => void;
    /** true når søk/filter er aktivt → vis treff; ellers vis «Nylig jobbet med». */
    isSearching: boolean;
    /** true mens planene hentes → header og søk vises, lista er skjelett. */
    isLoading?: boolean;
    /** Feilmelding når planlista for gjeldende søk/filter ikke kunne hentes. */
    listError?: string;
    /** Antall eller filtervalg kunne ikke hentes — lista virker, men tallene mangler. */
    statsError?: boolean;
    /** Søketeksten som faktisk er sendt (debouncet) — brukes i tomtilstanden. */
    searchTerm: string;
    recentItems: PlanSummary[];
    /** Lastede treff så langt — lista lastes 50 om gangen (#14578). */
    resultItems: PlanSummary[];
    /** Antall planer som treffer søk/filter i hele registeret. undefined mens det telles. */
    matchCount?: number;
    /** Antall planer i hele registeret. undefined mens det telles. */
    totalCount?: number;
    /** Det finnes flere treff enn de som er lastet. */
    hasMore: boolean;
    isLoadingMore: boolean;
    /** Forrige forsøk på å hente neste side feilet. */
    loadMoreError?: boolean;
    onLoadMore: () => void;
    onZoomTo: (napId: string) => void;
    onSelect: (napId: string) => void;
}

const PlanLandingPage: React.FC<PlanLandingPageProps> = ({
    onCreatePlan,
    search,
    filter,
    filterOpen,
    onToggleFilter,
    activeFilterCount,
    activeFilterChips,
    onClearAllFilters,
    sortOption,
    onSortChange,
    isSearching,
    isLoading,
    listError,
    statsError,
    searchTerm,
    recentItems,
    resultItems,
    matchCount,
    totalCount,
    hasMore,
    isLoadingMore,
    loadMoreError,
    onLoadMore,
    onZoomTo,
    onSelect,
}) => {
    const { t } = useTranslation();

    // Kortene oversetter ikke selv (én subscription her, ikke én per rad).
    // Strenger er primitive, så React.memo på kortet holder.
    const selectLabel = t('landing.recent.select');
    const ikkeGjeldendeLabel = t('planValidity.notCurrent');
    const cardSubtitle = (plan: PlanSummary) =>
        plan.sistEndret && plan.sistEndretAv
            ? t('landing.recent.lastEditedBy', { date: plan.sistEndret, user: plan.sistEndretAv })
            : plan.sistEndret
                ? t('landing.recent.lastEdited', { date: plan.sistEndret })
                : undefined;

    const renderCard = (plan: PlanSummary) => (
        <li key={plan.nasjonalArealplanId} className={styles.resultItem}>
            <PlanResultCard
                plan={plan}
                subtitle={cardSubtitle(plan)}
                selectLabel={selectLabel}
                ikkeGjeldendeLabel={ikkeGjeldendeLabel}
                onZoomTo={onZoomTo}
                onSelect={onSelect}
            />
        </li>
    );

    const renderList = (items: PlanSummary[], compact = false) => (
        <ul className={`${styles.results} ${compact ? styles.resultsCompact : ''}`}>
            {items.map(renderCard)}
        </ul>
    );

    const showLoadMore = hasMore || isLoadingMore || Boolean(loadMoreError);
    const renderPagedList = (items: PlanSummary[]) => (
        <>
            <ul className={`${styles.results} ${showLoadMore ? styles.resultsHasMore : ''}`}>
                {items.map(renderCard)}
            </ul>
            <LoadMoreTrigger
                className={styles.loadMore}
                hasMore={hasMore}
                isLoading={isLoadingMore}
                isError={loadMoreError}
                onLoadMore={onLoadMore}
            />
        </>
    );

    const allTitle = totalCount === undefined
        ? t('landing.all.titleLoading')
        : t('landing.all.title', { count: totalCount });

    return (
        <div className={styles.root}>
            <div className={styles.mainColumn}>
                <header className={styles.header}>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>{t('landing.title')}</h1>
                        <p className={styles.subtitle}>{t('landing.subtitle')}</p>
                    </div>
                    <Button variant="primary" onClick={onCreatePlan}>
                        {t('landing.createPlan')}
                    </Button>
                </header>

                <div className={styles.stickyControls}>
                    <div className={styles.searchRow}>
                        <div className={styles.searchField}>
                            <PlanSearchBar {...search} />
                        </div>
                        <Button
                            variant="secondary"
                            className={styles.filterButton}
                            onClick={onToggleFilter}
                            aria-expanded={filterOpen}
                            aria-controls="plan-filter-column"
                        >
                            <FunnelIcon size={16} aria-hidden />
                            {t('landing.filterToggle')}
                            {activeFilterCount > 0 && (
                                <span
                                    className={styles.filterCountBadge}
                                    aria-label={t('landing.activeFilters', { count: activeFilterCount })}
                                >
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                        <Dropdown
                            align="end"
                            className={styles.sortDropdown}
                            trigger={
                                <span className={styles.sortTrigger} aria-label={t('landing.sortLabel')}>
                                    <ArrowsDownUpIcon size={18} aria-hidden />
                                </span>
                            }
                            groups={[{
                                items: sortOptions.map(o => ({
                                    id: `${o.field}-${o.direction}`,
                                    label: t(o.labelKey),
                                    selected: o.field === sortOption.field && o.direction === sortOption.direction,
                                    onClick: () => onSortChange(o),
                                })),
                            }]}
                        />
                    </div>

                    {statsError && (
                        <Notice variant="warning" className={styles.empty}>
                            {t('landing.statsError')}
                        </Notice>
                    )}

                    {isSearching && !isLoading && (
                        <div className={styles.resultMeta}>
                            {matchCount !== undefined && totalCount !== undefined && (
                                <span className={styles.resultCount}>
                                    {t('landing.resultCount', { count: matchCount, total: totalCount })}
                                </span>
                            )}
                            {activeFilterChips.length > 0 && (
                                <>
                                    <div className={styles.filterChips}>
                                        {activeFilterChips.map(c => (
                                            <Chip key={c.id} onRemove={c.onRemove} removeLabel={`${t('common.close')}: ${c.label}`}>
                                                {c.label}
                                            </Chip>
                                        ))}
                                    </div>
                                    {activeFilterChips.length > 1 && (
                                        <button type="button" className={styles.clearAll} onClick={onClearAllFilters}>
                                            {t('landing.clearAll')}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {listError ? (
                    <Notice variant="danger" className={styles.empty}>
                        {listError}
                    </Notice>
                ) : isLoading ? (
                    <div className={styles.results} role="status" aria-label={t('common.loading')}>
                        {Array.from({ length: 6 }, (_, i) => (
                            <SkeletonCard key={i}>
                                <SkeletonLine width="min(50%, 320px)" />
                                <div className={styles.skeletonMeta}>
                                    <SkeletonLine width="min(30%, 160px)" height={12} />
                                    <SkeletonLine width={64} height={20} pill />
                                </div>
                            </SkeletonCard>
                        ))}
                    </div>
                ) : isSearching ? (
                    resultItems.length === 0 ? (
                        <Notice variant="info" className={styles.empty}>
                            {t('landing.searchResults.empty', { term: searchTerm })}
                        </Notice>
                    ) : (
                        renderPagedList(resultItems)
                    )
                ) : (
                    <>
                        {/* «Nylig» skjules når den er tom — hele lista ligger uansett under (F7.1.1, #14552). */}
                        {recentItems.length > 0 && (
                            <section className={styles.recentSection} aria-label={t('landing.recent.title')}>
                                <div className={styles.recentHeader}>
                                    <div className={styles.headerText}>
                                        <h2 className={styles.recentTitle}>{t('landing.recent.title')}</h2>
                                        <p className={styles.subtitle}>{t('landing.recent.subtitle')}</p>
                                    </div>
                                </div>
                                {renderList(recentItems, true)}
                            </section>
                        )}
                        {/* Standardlista: alle planer i tjenesten (tjenesten er per kommune), kontinuerlig
                            scroll uten paginering (F7.1.4, Feature 14544) — lastes 50 om gangen (#14578). */}
                        <section className={styles.recentSection} aria-label={allTitle}>
                            <div className={styles.recentHeader}>
                                <div className={styles.headerText}>
                                    <h2 className={styles.recentTitle}>{allTitle}</h2>
                                    <p className={styles.subtitle}>{t('landing.all.subtitle')}</p>
                                </div>
                            </div>
                            {resultItems.length === 0 ? (
                                <Notice variant="info" className={styles.empty}>
                                    {t('landing.all.empty')}
                                </Notice>
                            ) : (
                                renderPagedList(resultItems)
                            )}
                        </section>
                    </>
                )}
            </div>

            {filterOpen && (
                <aside id="plan-filter-column" className={styles.filterColumn} aria-label={t('landing.filterToggle')}>
                    <div className={styles.filterHeader}>
                        <h2 className={styles.filterTitle}>{t('landing.filterToggle')}</h2>
                        <IconButton
                            icon={<XIcon size={14} aria-hidden />}
                            label={t('landing.filterClose')}
                            variant="ghost"
                            size="sm"
                            onClick={onToggleFilter}
                        />
                    </div>
                    <div className={styles.filterContent}>
                        <PlanFilterPanel {...filter} />
                    </div>
                </aside>
            )}
        </div>
    );
};

export default PlanLandingPage;
